import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';

function PostScheduleCard() {
  const [schedule, setSchedule]     = useState(null);
  const [ruleState, setRuleState]   = useState(null);
  const [selected, setSelected]     = useState('');
  const [options, setOptions]       = useState([]);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState(null);
  const { showToast } = useToast();

  function load() {
    setError(null);
    api.getPostSchedule()
      .then(({ schedule: s, options: o, state: st }) => {
        setSchedule(s); setSelected(s); setOptions(o); setRuleState(st);
      })
      .catch(e => setError(e.message || 'Unable to load post schedule'));
  }

  useEffect(() => { load(); }, []); // intentionally run once on mount

  async function handleSave() {
    if (!selected || selected === schedule) return;
    setSaving(true);
    try {
      const { label } = await api.updatePostSchedule(selected);
      setSchedule(selected);
      setSaved(true);
      showToast(`Post frequency updated: ${label}`, 'success');
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      showToast('Failed to update: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleTogglePause() {
    const newState = ruleState === 'ENABLED' ? 'DISABLED' : 'ENABLED';
    setSaving(true);
    try {
      await api.setPostScheduleState(newState);
      setRuleState(newState);
      showToast(newState === 'DISABLED' ? 'Auto-posting paused' : 'Auto-posting resumed', 'success');
    } catch (e) {
      showToast('Failed: ' + e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const dirty  = selected && selected !== schedule;
  const paused = ruleState === 'DISABLED';

  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div className="card-header"><h3>🕐 Post Frequency</h3></div>
      <div className="card-body">
        <p style={{ fontSize: '0.9rem', marginBottom: 16, color: 'var(--muted)', lineHeight: 1.6 }}>
          Controls how often the AI Agent automatically generates and publishes a new post.
          Changes take effect immediately — no redeployment needed.
        </p>
        {error ? (
          <div style={{ fontSize: '0.9rem', color: '#c0392b' }}>
            Failed to load: {error}.{' '}
            <button onClick={load} style={{ background: 'none', border: 'none', color: 'var(--teal)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
              Retry
            </button>
          </div>
        ) : schedule === null ? (
          <div style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>Loading…</div>
        ) : (
          <>
            {paused && (
              <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff3cd', borderRadius: 8, fontSize: '0.88rem', color: '#856404' }}>
                ⏸ Auto-posting is paused — the agent will not publish new posts until resumed.
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <select
                value={selected}
                onChange={e => setSelected(e.target.value)}
                disabled={paused}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
                  fontSize: '0.95rem', background: 'var(--surface)', color: 'var(--text)',
                  cursor: paused ? 'not-allowed' : 'pointer', minWidth: 180,
                  opacity: paused ? 0.5 : 1,
                }}
              >
                {options.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={!dirty || saving || paused}
                style={{ minWidth: 100 }}
              >
                {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save'}
              </button>
              {!dirty && !paused && schedule && (
                <span style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                  Currently: <strong>{options.find(o => o.value === schedule)?.label || schedule}</strong>
                </span>
              )}
              <button
                className={`btn btn-sm ${paused ? 'btn-primary' : 'btn-secondary'}`}
                onClick={handleTogglePause}
                disabled={saving}
                style={{ marginLeft: 'auto' }}
              >
                {saving ? '…' : paused ? '▶ Resume posting' : '⏸ Pause posting'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const region = process.env.REACT_APP_REGION || 'us-east-1';
  const env    = process.env.REACT_APP_ENVIRONMENT || 'dev';

  const links = [
    { label: 'Secrets Manager — Facebook Token',  url: `https://${region}.console.aws.amazon.com/secretsmanager/secret?name=hca-facebook-token-${env}&region=${region}` },
    { label: 'EventBridge — Daily Post Rule',      url: `https://${region}.console.aws.amazon.com/scheduler/home?region=${region}#/schedules` },
    { label: 'Lambda Functions',                   url: `https://${region}.console.aws.amazon.com/lambda/home?region=${region}#/functions` },
    { label: 'DynamoDB Tables',                    url: `https://${region}.console.aws.amazon.com/dynamodbv2/home?region=${region}#tables` },
    { label: 'CloudWatch Logs',                    url: `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups` },
    { label: 'SNS Escalation Topic',               url: `https://${region}.console.aws.amazon.com/sns/v3/home?region=${region}#/topics` },
  ];

  return (
    <>
      <div className="page-header"><h1>Settings</h1></div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Post frequency — live configurable */}
        <PostScheduleCard />

        {/* Token refresh */}
        <div className="card" style={{ gridColumn: '1 / -1' }}>
          <div className="card-header"><h3>🔑 Facebook Token Refresh</h3></div>
          <div className="card-body">
            <p style={{ fontSize: '0.9rem', marginBottom: 16, lineHeight: 1.7 }}>
              Facebook Page Access Tokens expire after <strong>~60 days</strong>. Run this command in your VS Code terminal to update the token in AWS Secrets Manager:
            </p>
            <div style={{ background: 'var(--navy)', color: '#e2e8f0', padding: '14px 18px', borderRadius: 10, fontFamily: 'monospace', fontSize: '0.85rem' }}>
              cd C:\Users\Homec\hca-marketing-agent<br />
              .\scripts\set-token.ps1
            </div>
            <p style={{ marginTop: 12, fontSize: '0.82rem', color: 'var(--muted)' }}>
              Or update directly via AWS CLI — see the README for the full command.
            </p>
          </div>
        </div>

        {/* Agent status */}
        <div className="card">
          <div className="card-header"><h3>🤖 Agent Configuration</h3></div>
          <div className="card-body">
            <table style={{ width: '100%', fontSize: '0.88rem' }}>
              <tbody>
                {[
                  ['Environment',    env],
                  ['AWS Region',     region],
                  ['Post schedule',  'See above ↑'],
                  ['Poll interval',  'Every 15 minutes'],
                  ['Bedrock model',  'Claude Sonnet 4.6'],
                ].map(([k, v]) => (
                  <tr key={k}>
                    <td style={{ color: 'var(--muted)', paddingBottom: 10, paddingRight: 20, whiteSpace: 'nowrap' }}>{k}</td>
                    <td style={{ fontWeight: 500 }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AWS console links */}
        <div className="card">
          <div className="card-header"><h3>☁️ AWS Console Links</h3></div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {links.map(l => (
              <a key={l.label} href={l.url} target="_blank" rel="noreferrer"
                style={{ fontSize: '0.88rem', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6 }}>
                ↗ {l.label}
              </a>
            ))}
          </div>
        </div>

        {/* Safety reminders */}
        <div className="card" style={{ gridColumn: '1 / -1', borderLeft: '4px solid var(--teal)' }}>
          <div className="card-header"><h3>✅ Safety Reminders</h3></div>
          <div className="card-body">
            <ul style={{ fontSize: '0.9rem', lineHeight: 2, paddingLeft: 20, color: 'var(--text)' }}>
              <li>This agent posts <strong>organic unpaid content only</strong> — no ads, no boosting</li>
              <li>Nothing is ever deleted — posts, comments, messages, and reviews are permanent</li>
              <li>Sensitive messages are <strong>escalated to your email</strong>, never auto-replied</li>
              <li>Every action is logged in the Audit Log tab and DynamoDB</li>
              <li>Facebook token expires ~60 days after creation — refresh before it expires</li>
            </ul>
          </div>
        </div>

      </div>
    </>
  );
}
